<script>
import { defineAsyncComponent } from 'vue';
import {
  buildEditor,
  EditorView,
  ArticleMarkdownSerializer,
  ArticleMarkdownTransformer,
  EditorState,
  Selection,
} from '@chatwoot/prosemirror-schema';
import {
  richTextSchemaWithVariables,
  textToVariableNodes,
  variableNodesToText,
  getVariableCategory,
} from '../helpers/variableChips';
import {
  suggestionsPlugin,
  triggerCharacters,
} from '@chatwoot/prosemirror-schema/src/mentions/plugin';
import {
  insertAtCursor,
  scrollCursorIntoView,
  getContentNode,
} from 'dashboard/helper/editorHelper';
import { useUISettings } from 'dashboard/composables/useUISettings';
import KeyboardEmojiSelector from 'dashboard/components/widgets/WootWriter/keyboardEmojiSelector.vue';
import Dropdown from '../../../../components/Dropdown.vue';
import variablesMixins from '../../variablesDropdown/variablesMixins';
const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);
import VariablesDropdown from '../../variablesDropdown/Index.vue';
import { WGPT_AUTOMATIONS_RICHTEXT_EDITOR_MENU_OPTIONS } from '../helpers/constants';
import { resolveWrapperPath } from '../../../helpers/variablePathResolver';

import ButtonNext from 'next/button/Button.vue';
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';

const editorSchema = richTextSchemaWithVariables;

const createState = (
  content,
  placeholder,
  // eslint-disable-next-line default-param-last
  plugins = [],
  // eslint-disable-next-line default-param-last
  methods = {},
  enabledMenuOptions = WGPT_AUTOMATIONS_RICHTEXT_EDITOR_MENU_OPTIONS,
  resolveLabel = (name, prefix) => ({
    label: name,
    category: getVariableCategory(name, prefix),
  })
) => {
  // Adds '\' before '.' (e.g. %{contact.name} => %{contact\.name})
  const safeContent = content.replace(
    /([%#])\{([^{}]*?\.[^{}]*?)\}/g,
    (match, prefix, p1) => {
      const escapedVariable = p1.replace(/\./g, '\\.');
      return `${prefix}{${escapedVariable}}`;
    }
  );
  const doc = new ArticleMarkdownTransformer(editorSchema).parse(safeContent);
  const docWithChips = textToVariableNodes(doc, editorSchema, resolveLabel);
  return EditorState.create({
    doc: docWithChips,
    plugins: buildEditor({
      schema: editorSchema,
      placeholder,
      methods,
      plugins,
      enabledMenuOptions,
    }),
  });
};

let editorView = null;
let state;

export default {
  components: {
    KeyboardEmojiSelector,
    Dropdown,
    EmojiIconPicker,
    VariablesDropdown,
    ButtonNext,
  },
  mixins: [variablesMixins],
  props: {
    modelValue: { type: String, default: '' },
    editorId: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    variables: { type: Array, default: () => [] },
    autofocus: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['blur', 'input', 'update:modelValue', 'keyup', 'focus', 'keydown'],
  setup() {
    const { uiSettings, updateUISettings } = useUISettings();
    const { toLocale } = useAutomationLocales();

    return {
      uiSettings,
      updateUISettings,
      toLocale,
    };
  },
  data() {
    return {
      isTextSelected: false, // Tracks text selection and prevents unnecessary re-renders on mouse selection
      showEmojiMenu: false,
      emojiSearchTerm: '',
      editorView: null,
      updateSelectionWith: '',
      range: {
        from: 1,
        to: 2,
      },
      state: undefined,
      enabledSuggestion: true,
      chipReplacePos: null,
      selectedVariable: '',
    };
  },
  computed: {
    isFetchingWrapperData() {
      const flags = this.$store.getters['wgptAutomationRegistry/getUIFlags'];
      return flags.isFetchingFields || flags.isFetchingCustomObjects;
    },
    plugins() {
      return [
        suggestionsPlugin({
          matcher: triggerCharacters('%'),
          suggestionClass: '',
          onEnter: args => this.onSuggestionsPluginEnter(args, '%'),
          onChange: this.onSuggestionsPluginChange,
          onExit: this.onSuggestionsPluginExit,
          onKeyDown: this.onSuggestionsPluginKeyDown,
        }),
        suggestionsPlugin({
          matcher: triggerCharacters('#'),
          suggestionClass: '',
          onEnter: args => this.onSuggestionsPluginEnter(args, '#'),
          onChange: this.onSuggestionsPluginChange,
          onExit: this.onSuggestionsPluginExit,
          onKeyDown: this.onSuggestionsPluginKeyDown,
        }),
        suggestionsPlugin({
          matcher: triggerCharacters(':', 2), // Trigger after ':' and at least 2 characters
          suggestionClass: '',
          onEnter: args => {
            if (!this.enabledSuggestion) return false;

            this.showEmojiMenu = true;
            this.emojiSearchTerm = args.text || '';
            this.range = args.range;
            editorView = args.view;
            this.variablesDropdownEvent = {
              target: editorView.dom,
              eventName: 'suggestion',
            };
            return false;
          },
          onChange: args => {
            editorView = args.view;
            this.range = args.range;
            this.emojiSearchTerm = args.text;
            return false;
          },
          onExit: () => {
            this.emojiSearchTerm = '';
            this.showEmojiMenu = false;
            return false;
          },
          onKeyDown: ({ event }) => {
            return event.keyCode === 13 && this.showEmojiMenu;
          },
        }),
      ];
    },
  },
  watch: {
    modelValue(newValue = '') {
      if (newValue !== this.contentFromEditor()) {
        this.reloadState();
      }
    },
    editorId() {
      this.reloadState();
    },
    isFetchingWrapperData(newVal, oldVal) {
      if (oldVal && !newVal) {
        this.refreshChipLabels();
      }
    },
    updateSelectionWith(value, valueBefore) {
      if (!editorView) return;

      if (value !== valueBefore && value !== '') {
        this.enabledSuggestion = false;
        const node = editorView.state.schema.text(value);
        const tr = editorView.state.tr.replaceSelectionWith(node);
        editorView.focus();
        state = editorView.state.apply(tr);
        editorView.updateState(state);
        this.emitOnChange();
        this.updateSelectionWith = '';
        this.enabledSuggestion = true;
      }
    },
  },

  created() {
    state = createState(
      this.modelValue,
      this.placeholder,
      this.plugins,
      {},
      WGPT_AUTOMATIONS_RICHTEXT_EDITOR_MENU_OPTIONS,
      this.resolveLabel.bind(this)
    );
  },
  mounted() {
    this.createEditorView();

    editorView.updateState(state);
    if (this.autofocus) {
      this.focusEditorInputField();
    }
  },
  methods: {
    contentFromEditor() {
      if (editorView) {
        const plainDoc = variableNodesToText(
          editorView.state.doc,
          editorSchema
        );
        return ArticleMarkdownSerializer.serialize(plainDoc);
      }
      return '';
    },
    refreshChipLabels() {
      if (!editorView) return;
      const cursorPos = this.lastCursorPos ?? 1;
      const content = this.contentFromEditor();
      state = createState(
        content,
        this.placeholder,
        this.plugins,
        {},
        WGPT_AUTOMATIONS_RICHTEXT_EDITOR_MENU_OPTIONS,
        this.resolveLabel.bind(this)
      );
      editorView.updateState(state);
      // Restore cursor position after state rebuild
      try {
        const pos = Math.min(cursorPos, state.doc.content.size);
        const $pos = state.doc.resolve(pos);
        const sel = Selection.near($pos);
        const tr = state.tr.setSelection(sel);
        state = state.apply(tr);
        editorView.updateState(state);
      } catch {
        // Position might be invalid after rebuild, ignore
      }
    },
    reloadState() {
      state = createState(
        this.modelValue,
        this.placeholder,
        this.plugins,
        {},
        WGPT_AUTOMATIONS_RICHTEXT_EDITOR_MENU_OPTIONS,
        this.resolveLabel.bind(this)
      );
      editorView.updateState(state);
      this.focusEditorInputField();
    },
    createEditorView() {
      editorView = new EditorView(this.$refs.editor, {
        state: state,
        dispatchTransaction: tx => {
          state = state.apply(tx);
          editorView.updateState(state);
          this.lastCursorPos = state.selection.from;
          if (tx.docChanged) {
            this.emitOnChange();
          }
          this.checkSelection(state);
        },
        handleDOMEvents: {
          keyup: this.onKeyup,
          focus: this.onFocus,
          blur: this.onBlur,
          keydown: this.onKeydown,
          click: this.onChipClick,
          paste: (view, event) => {
            const data = event.clipboardData.files;
            if (data.length > 0) {
              data.forEach(file => {
                // Check if the file is an image
                if (file.type.includes('image')) {
                  /* empty */
                }
              });
              event.preventDefault();
            }
          },
        },
      });
    },
    handleKeyEvents() {},
    focusEditorInputField() {
      const { tr } = editorView.state;
      const selection = Selection.atEnd(tr.doc);

      editorView.dispatch(tr.setSelection(selection));
      editorView.focus();
    },
    insertSpecialContent(type, content) {
      if (!editorView) {
        return;
      }

      let { node, from, to } = getContentNode(
        editorView,
        type,
        content,
        this.range,
        {}
      );

      if (!node) return;

      this.insertNodeIntoEditor(node, from, to);
    },
    emitOnChange() {
      const content = this.contentFromEditor();
      this.$emit('update:modelValue', content);
      this.$emit('input', content);
    },
    onKeyup() {
      this.$emit('keyup');
    },
    onKeydown() {
      this.$emit('keydown');
    },
    onBlur() {
      this.$emit('blur');
    },
    onFocus() {
      this.$emit('focus');
    },
    checkSelection(editorState) {
      const { from, to } = editorState.selection;
      // Check if there's a selection (from and to are different)
      const hasSelection = from !== to;
      // If the selection state is the same as the previous state, do nothing
      if (hasSelection === this.isTextSelected) return;
      // Update the selection state
      this.isTextSelected = hasSelection;

      const { editor } = this.$refs;

      // Toggle the 'has-selection' class based on whether there's a selection
      editor.classList.toggle('has-selection', hasSelection);
      // If there's a selection, update the menubar position
      if (hasSelection) this.setMenubarPosition(editorState);
    },
    setMenubarPosition(editorState) {
      if (!editorState.selection) return;

      // Get the start and end positions of the selection
      const { from, to } = editorState.selection;
      const { editor } = this.$refs;
      // Get the editor's position relative to the viewport
      const { left: editorLeft, top: editorTop } =
        editor.getBoundingClientRect();

      // Get the editor's width
      const editorWidth = editor.offsetWidth;
      const menubarWidth = 480; // Menubar width (adjust as needed (px))

      // Get the end position of the selection
      const { bottom: endBottom, right: endRight } = editorView.coordsAtPos(to);
      // Get the start position of the selection
      const { left: startLeft } = editorView.coordsAtPos(from);

      // Calculate the top position for the menubar (10px below the selection)
      const top = endBottom - editorTop + 10;
      // Calculate the left position for the menubar
      // This centers the menubar on the selection while keeping it within the editor's bounds
      const left = Math.max(
        0,
        Math.min(
          (startLeft + endRight) / 2 - editorLeft,
          editorWidth - menubarWidth
        )
      );
      // Set the CSS custom properties for positioning the menubar
      editor.style.setProperty('--selection-top', `${top}px`);
      editor.style.setProperty('--selection-left', `${left}px`);
    },
    insertNodeIntoEditor(node, from = 0, to = 0) {
      state = insertAtCursor(editorView, node, from, to);
      this.emitOnChange();
      this.$nextTick(() => {
        scrollCursorIntoView(editorView);
      });
    },
    resolveLabel(name, prefix) {
      const variables = this.variables || [];
      const baseCategory = getVariableCategory(name, prefix);
      const parts = name.split('.');
      if (parts.length > 1) {
        const parentName = parts[0];
        const childName = parts.slice(1).join('.');
        const parent = variables.find(
          v => v.name === parentName && (v.prefix ?? '%') === (prefix ?? '%')
        );
        if (parent && parent.wrapper) {
          const child = this.resolveWrapperChild(parent, childName);
          if (child) {
            const parentLabel = this.toLocale(parent.label, parent.name);
            return {
              label: `${parentLabel} > ${child.label}`,
              category: baseCategory,
            };
          }
        }
        return { label: name, category: 'invalid' };
      }
      const variable = variables.find(
        v => v.name === name && (v.prefix ?? '%') === (prefix ?? '%')
      );
      if (variable) {
        return {
          label: this.toLocale(variable.label, variable.name),
          category: baseCategory,
        };
      }
      return { label: name, category: 'invalid' };
    },
    resolveWrapperChild(parent, childName) {
      // Walks the remaining path segments; custom objects can nest other
      // custom objects (e.g. car_listing.make.name).
      const { status, chain } = resolveWrapperPath(
        parent,
        childName.split('.'),
        {
          getCustomObjects:
            this.$store.getters['wgptAutomationRegistry/getCustomObjects'],
          getDynamicOptions:
            this.$store.getters['wgptAutomationRegistry/getDynamicOptions'],
          toLocale: this.toLocale,
        }
      );
      if (status === 'pending') return { label: childName, pending: true };
      if (status !== 'resolved') return null;
      return { label: chain.map(node => node.resolvedLabel).join(' > ') };
    },
    closeVariablesDropdownAndResetChip() {
      this.showVariablesDropdown = false;
      this.chipReplacePos = null;
      this.selectedVariable = '';
    },
    onChipClick(_view, event) {
      const chip = event.target.closest('.prosemirror-variable-chip');
      if (!chip) return false;

      const pos = editorView.posAtDOM(chip, 0);
      const resolved = editorView.state.doc.resolve(pos);
      const node = resolved.nodeAfter;
      if (!node || node.type.name !== 'variable') return false;

      this.chipReplacePos = { from: pos, to: pos + node.nodeSize };
      this.selectedVariable = node.attrs.name;
      this.variablesFilter = node.attrs.prefix;
      this.variablesDropdownEvent = {
        target: chip,
        eventName: 'click',
      };
      this.showVariablesDropdown = true;
      return true;
    },
    variableWithSpacing(from, to, variableNode) {
      const { doc } = editorView.state;
      const nodes = [];

      const $from = doc.resolve(from);
      const nodeBefore = $from.nodeBefore;
      if (
        nodeBefore &&
        (nodeBefore.isText ? !/\s$/.test(nodeBefore.text) : true)
      ) {
        nodes.push(editorSchema.text(' '));
      }

      nodes.push(variableNode);

      const $to = doc.resolve(to);
      const nodeAfter = $to.nodeAfter;
      const hasSpaceAfter =
        nodeAfter && nodeAfter.isText && /^\s/.test(nodeAfter.text);
      if (!hasSpaceAfter) {
        nodes.push(editorSchema.text(' '));
      }

      return nodes;
    },
    onVariableAdd(_updatedValue, variableName, variablePrefix) {
      if (!editorView) return;

      const resolved = this.resolveLabel(variableName, variablePrefix);
      const node = editorSchema.nodes.variable.create({
        name: variableName,
        prefix: variablePrefix,
        label: resolved.label || variableName,
        category: resolved.category,
      });

      if (this.chipReplacePos) {
        const { from, to } = this.chipReplacePos;
        const tr = editorView.state.tr.replaceWith(from, to, node);
        state = editorView.state.apply(tr);
        editorView.updateState(state);
        editorView.focus();
        this.emitOnChange();
        this.chipReplacePos = null;
        return;
      }

      if (this.variablesDropdownEvent.eventName === 'suggestion') {
        const { from, to } = this.range;
        const nodes = this.variableWithSpacing(from, to, node);
        const tr = editorView.state.tr.replaceWith(from, to, nodes);
        state = editorView.state.apply(tr);
        editorView.updateState(state);
        editorView.focus();
        this.emitOnChange();
        return;
      }

      const pos = this.lastCursorPos ?? editorView.state.selection.from;
      const nodes = this.variableWithSpacing(pos, pos, node);
      const tr = editorView.state.tr.replaceWith(pos, pos, nodes);
      state = editorView.state.apply(tr);
      editorView.updateState(state);
      editorView.focus();
      this.emitOnChange();
    },
    openVariablesDropdownManually() {
      const event = {
        target: editorView.dom,
        eventName: 'click',
      };
      this.openVariablesDropdown(event);
    },
    openEmojisDropdownManually(e) {
      this.variablesDropdownEvent = e;
      this.showEmojiMenu = true;
    },
    hideEmojiPickerManually() {
      this.showEmojiMenu = false;
    },
    addIntoEditor(content) {
      this.updateSelectionWith = content;
    },
    onSuggestionsPluginEnter(args, char) {
      if (!this.enabledSuggestion) return false;

      this.showVariablesDropdown = true;
      this.variablesFilter = char;
      this.range = args.range;
      editorView = args.view;
      this.variablesDropdownEvent = {
        target: args.view.dom,
        eventName: 'suggestion',
      };
      return false;
    },
    onSuggestionsPluginChange(args) {
      editorView = args.view;
      this.range = args.range;

      this.variableSearchTerm = args.text;
      return false;
    },
    onSuggestionsPluginExit() {
      this.variableSearchTerm = '';
      this.showVariablesDropdown = false;
      return false;
    },
    onSuggestionsPluginKeyDown({ event }) {
      return event.keyCode === 13 && this.showVariablesDropdown;
    },
  },
};
</script>

<template>
  <div class="editor-root">
    <Dropdown
      v-if="emojiSearchTerm"
      :show-dropdown="showEmojiMenu"
      :event="variablesDropdownEvent"
      :show-backdrop="false"
      @close="showEmojiMenu = false"
    >
      <KeyboardEmojiSelector
        v-if="showEmojiMenu"
        class="w-max max-w-[300px]"
        :search-key="emojiSearchTerm"
        @select-emoji="emoji => insertSpecialContent('emoji', emoji)"
      />
    </Dropdown>
    <Dropdown
      v-else
      :show-dropdown="showEmojiMenu"
      :event="variablesDropdownEvent"
      position="start"
      class="emoji-dropdown"
      @close="showEmojiMenu = false"
    >
      <EmojiIconPicker
        v-if="showEmojiMenu"
        mode="emoji"
        class="emoji-input !top-0 !right-0 !static"
        @select="addIntoEditor($event.value)"
      />
    </Dropdown>

    <div ref="editor" />

    <div class="py-3 z-10 flex gap-2">
      <ButtonNext
        v-tooltip.top-end="
          $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.TIP_EMOJI_ICON')
        "
        :title="
          $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.TIP_EMOJI_ICON')
        "
        icon="i-lucide-smile-plus"
        color="slate"
        size="xs"
        @click="openEmojisDropdownManually"
      />
      <ButtonNext
        v-tooltip.top-end="
          $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.TIP_VARIABLE_ICON')
        "
        :title="
          $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.DYNAMIC_FIELDS.TIP_VARIABLE_ICON')
        "
        icon="i-lucide-braces"
        color="slate"
        size="xs"
        @click="openVariablesDropdownManually"
      />
    </div>

    <VariablesDropdown
      :show-dropdown="showVariablesDropdown"
      :event="variablesDropdownEvent"
      :variables="variables"
      :filter-by-prefix="variablesFilter"
      :selected="selectedVariable"
      @close="closeVariablesDropdownAndResetChip"
      @add="addVariable"
    />
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .ProseMirror-menubar-wrapper {
    @apply flex flex-col gap-3;

    .ProseMirror-menubar {
      min-height: 1.25rem !important;
      @apply items-center gap-4 flex pb-0 bg-transparent text-n-slate-11 relative ltr:left-[3px] rtl:right-[3px];

      .ProseMirror-menu-active {
        @apply bg-n-slate-5 dark:bg-n-solid-3 !important;
      }

      .ProseMirror-menuitem {
        @apply mr-0 size-4 flex items-center justify-center;

        .ProseMirror-icon {
          @apply size-4 flex items-center justify-center flex-shrink-0;

          svg {
            @apply size-full;
          }
        }
      }
    }

    .ProseMirror-menubar:not(:has(*)) {
      max-height: none !important;
      min-height: 0 !important;
      padding: 0 !important;
    }
  }

  > .ProseMirror {
    @apply p-0 break-words text-n-slate-12;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    p {
      @apply text-n-slate-12;
    }

    blockquote {
      @apply border-n-slate-7;

      p {
        @apply text-n-slate-11;
      }
    }

    ol li {
      @apply list-item list-decimal;
    }
  }

  .ProseMirror-woot-style {
    @apply overflow-auto min-h-[5rem] max-h-[10rem];
  }
}

.button--only-icon ::v-deep {
  .button__content,
  .icon {
    @apply w-max pointer-events-none;
  }
}

.emoji-dropdown ::v-deep .dropdown-pane {
  @apply mx-2;
}

.emoji-input::before {
  right: -1em;
  transform: rotate(-90deg);
  bottom: 0.75rem;
  display: none;
}

::v-deep .prosemirror-variable-chip {
  @apply inline-flex items-center px-1.5 py-0 rounded text-xs font-medium
    cursor-pointer transition-colors align-baseline border;

  &--normal {
    @apply bg-n-alpha-2 text-n-brand border-n-weak;
  }

  &--action {
    @apply bg-n-violet-3 text-n-violet-9 border-n-violet-6;
  }

  &--system {
    @apply bg-n-slate-3 text-n-slate-11 border-n-slate-7;
  }

  &--invalid {
    @apply bg-n-ruby-2 text-n-ruby-11 border-n-ruby-8;
  }
}
</style>
